/**
 * Check if a subscription has expired
 * @param {string} expiryDate - ISO date string of expiry date
 * @returns {boolean} - True if expired or no date provided
 */
export const isExpired = (expiryDate) => {
  if (!expiryDate) return true;

  try {
    const expiry = new Date(expiryDate);
    // Check if date is invalid
    if (isNaN(expiry.getTime())) {
      console.error("Error evaluating expiry state: Invalid date");
      return true;
    }
    const now = new Date();
    return now > expiry;
  } catch (err) {
    console.error("Error evaluating expiry state:", err);
    return true; // Default to expired if date parsing fails
  }
};

/**
 * Check if current date is within the first 14 days of the subscription
 * Assumes a 1-year subscription period
 * @param {string} startDate - ISO date string of start date
 * @returns {boolean} - True if within first 14 days from start
 */
export const isInFirst14Days = (startDate) => {
  if (!startDate) return false;

  try {
    const start = new Date(startDate);
    // Check if date is invalid
    if (isNaN(start.getTime())) {
      console.error("Error evaluating first 14 days window: Invalid date");
      return false;
    }
    const now = new Date();

    // Calculate 14 days from start date
    const fourteenDaysFromStart = new Date(startDate);
    fourteenDaysFromStart.setDate(start.getDate() + 14);

    // Check if current date is within first 14 days
    return now >= start && now <= fourteenDaysFromStart;
  } catch (err) {
    console.error("Error evaluating first 14 days window:", err);
    return false;
  }
};

/**
 * Check if current date is within the renewal period (1 month before expiry)
 * @param {string} nextRenewalDate - ISO date string of next renewal date
 * @returns {boolean} - True if within 1 month before next renewal date
 */
export const isInRenewalPeriod = (nextRenewalDate) => {
  if (!nextRenewalDate) return false;

  try {
    const expiry = new Date(nextRenewalDate);
    // Check if date is invalid
    if (isNaN(expiry.getTime())) {
      console.error("Error evaluating renewal window: Invalid date");
      return false;
    }
    const now = new Date();

    // 1 month before expiry
    const oneMonthBefore = new Date(expiry);
    oneMonthBefore.setMonth(expiry.getMonth() - 1);

    return now >= oneMonthBefore && now <= expiry;
  } catch (err) {
    console.error("Error evaluating renewal window:", err);
    return false;
  }
};

export const isRenewable = (nextRenewalDate, cancelDate) => {
  return isInRenewalPeriod(nextRenewalDate) && Boolean(cancelDate); // if canceldate exist then show renew button otherwise not show
};

/**
 * Check if cancel date was within the first 14 days from start date
 * @param {string} startDate - ISO date string of start date
 * @param {string} cancelDate - ISO date string of cancel date
 * @returns {boolean} - True if cancel date is within 14 days of start date
 */
export const wasCancelledInFirst14Days = (startDate, cancelDate) => {
  if (!startDate || !cancelDate) return false;

  try {
    const start = new Date(startDate);
    const cancel = new Date(cancelDate);

    // Check if dates are invalid
    if (isNaN(start.getTime()) || isNaN(cancel.getTime())) {
      console.error("Error evaluating cancel date in first 14 days: Invalid date");
      return false;
    }

    // Calculate 14 days from start date
    const fourteenDaysFromStart = new Date(start);
    fourteenDaysFromStart.setDate(start.getDate() + 14);
    // can check also canceldate===EndDate
    // Check if cancel date is within first 14 days
    return cancel >= start && cancel <= fourteenDaysFromStart;
  } catch (err) {
    console.error("Error evaluating cancel date in first 14 days:", err);
    return false;
  }
};

export const shouldShowRenewButton = (nextRenewalDate, startDate, cancelDate, expiryDate) => {
  const isCancelled = Boolean(cancelDate);
  // If cancelled during first 14 days (cooling-off period) show renew button
  if (isCancelled && wasCancelledInFirst14Days(startDate, cancelDate)) return true;

  // Show only when: in renewal period OR expired OR already cancelled (but not in cooling-off)
  return isRenewable(nextRenewalDate, cancelDate) || isExpired(expiryDate) || isCancelled;
};

export const shouldShowCancelButton = (nextRenewalDate, startDate, cancelDate, expiryDate) => {
  const isCancelled = Boolean(cancelDate);

  const inFirst14Days = isInFirst14Days(startDate);
  const inRenewalPeriod = isInRenewalPeriod(nextRenewalDate);
  // Must be in first 14 days OR in renewal period
  if (!inFirst14Days && !inRenewalPeriod) return false;

  // Don't show if already cancelled
  if (isCancelled) return false;

  // If in renewal period and renew button is showing, don't show cancel
  if (inRenewalPeriod && shouldShowRenewButton(nextRenewalDate, startDate, cancelDate, expiryDate))
    return false;
  if (inFirst14Days || inRenewalPeriod) return true;
  return false;
};
